import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { Link } from "react-router-dom";
import { ShieldWarning, ShieldCheck, Warning, CheckCircle, FileText, ArrowRight } from "@phosphor-icons/react";

const RISK_COLORS = {
  alto: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
  medio: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  bajo: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
  ninguno: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
};
const STATUS_LABEL = { sin_documento: "Sin documento", vencido: "Vencido", proximo: "Próximo a vencer", vigente: "Vigente" };

export default function Compliance() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("todos");

  useEffect(() => { api.get("/compliance/check").then(r => setData(r.data)); }, []);

  if (!data) return <div className="text-center py-16 text-neutral-400">Analizando cumplimiento...</div>;

  const items = data.items.filter(i => filter === "todos" || i.risk === filter);
  const scoreColor = data.score >= 80 ? "text-emerald-600" : data.score >= 60 ? "text-amber-600" : "text-rose-600";
  const scoreBgRing = data.score >= 80 ? "stroke-emerald-500" : data.score >= 60 ? "stroke-amber-500" : "stroke-rose-500";

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Inteligencia regulatoria</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Asistente de cumplimiento</h1>
        <p className="text-neutral-500 mt-2">Detección automática de obligaciones sin documento vigente y scoring de riesgo.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white rounded-md p-6 flex items-center gap-5" data-testid="compliance-score-card">
          <svg className="w-24 h-24 -rotate-90">
            <circle cx="48" cy="48" r="40" strokeWidth="8" className="stroke-white/20" fill="none"/>
            <circle cx="48" cy="48" r="40" strokeWidth="8" className={scoreBgRing} fill="none" strokeDasharray={251.3} strokeDashoffset={251.3 - (251.3 * data.score / 100)} strokeLinecap="round"/>
          </svg>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/70 font-semibold">Cumplimiento</div>
            <div className="font-display text-5xl font-black tracking-tighter" data-testid="compliance-score">{data.score}%</div>
            <div className="text-sm text-white/80 mt-1">{data.covered} de {data.total_obligations} cubiertas</div>
          </div>
        </div>
        <RiskCard label="Riesgo alto" value={data.risk_high} risk="alto" icon={Warning} testid="risk-high"/>
        <RiskCard label="Riesgo medio" value={data.risk_medium} risk="medio" icon={ShieldWarning} testid="risk-medium"/>
        <RiskCard label="Cubiertas" value={data.covered} risk="ninguno" icon={ShieldCheck} testid="risk-covered"/>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["todos","alto","medio","bajo","ninguno"].map(f => (
          <button key={f} onClick={()=>setFilter(f)}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${filter===f?'bg-neutral-700 text-white':'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`}
            data-testid={`filter-${f}`}>
            {f==="todos"?"Todas":`Riesgo ${f}`}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {items.map((it, idx) => {
          const c = RISK_COLORS[it.risk];
          return (
            <div key={idx} className={`bg-white border ${c.border} rounded-md p-5 flex flex-wrap items-center gap-4 hover:-translate-y-0.5 transition-all`} data-testid={`compliance-item-${idx}`}>
              <span className={`w-2 h-12 rounded-sm ${c.dot}`}></span>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg font-bold truncate">{it.obligation.name}</h3>
                <div className="flex flex-wrap gap-2 text-xs mt-1">
                  <span className="text-neutral-500">{it.obligation.authority}</span>
                  <span className="text-neutral-300">·</span>
                  <span className="text-neutral-500">{it.obligation.frequency}</span>
                  {it.installation && <><span className="text-neutral-300">·</span><span className="text-neutral-500">{it.installation.name}</span></>}
                </div>
              </div>
              <div className="text-right">
                <span className={`px-2.5 py-1 ${c.bg} ${c.text} rounded-md text-xs font-bold uppercase tracking-wide`}>{STATUS_LABEL[it.status]}</span>
                {it.days_to_expire !== null && <div className="text-xs text-neutral-500 mt-1">{it.days_to_expire < 0 ? `Hace ${Math.abs(it.days_to_expire)}d` : `En ${it.days_to_expire}d`}</div>}
                <div className="text-xs text-neutral-400 mt-1">{it.docs_count} doc(s)</div>
              </div>
              {it.best_document && (
                <Link to={`/documentos/${it.best_document.id}`} className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700">
                  Ver <ArrowRight size={12}/>
                </Link>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="py-12 text-center text-neutral-400 border border-dashed border-neutral-300 rounded-md"><CheckCircle size={42} className="mx-auto mb-3 text-emerald-400" weight="duotone"/><p>Sin obligaciones en esta categoría.</p></div>}
      </div>
    </div>
  );
}

function RiskCard({ label, value, risk, icon: Icon, testid }) {
  const c = RISK_COLORS[risk];
  return (
    <div className={`bg-white border ${c.border} rounded-md p-5`} data-testid={testid}>
      <div className="flex justify-between items-start mb-3">
        <span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{label}</span>
        <div className={`w-9 h-9 rounded-md ${c.bg} ${c.text} flex items-center justify-center`}><Icon size={18} weight="duotone"/></div>
      </div>
      <div className="font-display text-4xl font-black tracking-tighter">{value}</div>
    </div>
  );
}
